import React, { useEffect, useState } from "react";
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import "./FireWatch.css"; // Importing the CSS file
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import * as XLSX from "xlsx";


const FireWatch = ({ userRole }) => {
  const [fireWatchJobs, setFireWatchJobs] = useState([]);
  const [users, setUsers] = useState([]); // State to store users
  const [newFireWatchJob, setNewFireWatchJob] = useState({
    siteName: "",
    address: "",
    patrolStart: "",
    patrolEnd: "",
    techAssigned: "",
    fileUpload: [], // Add file upload field
    date: "", // Add date field
  });
  const [selectedJob, setSelectedJob] = useState(null); // For job details
  const [isEditing, setIsEditing] = useState(false); // Inline editing state
  const [editedJob, setEditedJob] = useState({});
  const [showAddJobPopup, setShowAddJobPopup] = useState(false); // State to manage popup visibility
  const [showDetailsPopup, setShowDetailsPopup] = useState(false); // State to manage details popup visibility
  const [showPatrolPopup, setShowPatrolPopup] = useState(false); // State to manage patrol popup visibility
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false); // State to manage delete confirmation popup
  const [isUploading, setIsUploading] = useState(false); // State to manage file upload status
  const [currentPage, setCurrentPage] = useState(1); // State to manage pagination
  const [properties, setProperties] = useState([]); // State to store properties
  const [patrolMode, setPatrolMode] = useState("single"); 
  // "single" | "range"
  
  const [timeFrame, setTimeFrame] = useState({
    start: "",
    end: "",
    interval: 15,
  });
  
  const storage = getStorage();

  const handleFileUploadForEdit = async (e) => {
    const files = e.target.files;
    const uploadedFiles = [...(editedJob.fileUpload || [])];
    setIsUploading(true);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const storageRef = ref(storage, `fireWatch/${editedJob.siteName}/${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      uploadedFiles.push(downloadURL);
    }

    setEditedJob((prev) => ({ ...prev, fileUpload: uploadedFiles }));
    setIsUploading(false);
  };

  const handleDeleteImage = async (index) => {
    const fileURL = editedJob.fileUpload[index];
    const fileRef = ref(storage, fileURL);

    try {
      await deleteObject(fileRef);
      const updatedFiles = editedJob.fileUpload.filter((_, i) => i !== index);
      setEditedJob((prev) => ({ ...prev, fileUpload: updatedFiles }));
    } catch (error) {
      console.error("Error deleting file:", error);
    }
  };

  useEffect(() => {
    const fetchFireWatchJobs = async () => {
      try {
        const fireWatchRef = collection(db, "fireWatch");
        const querySnapshot = await getDocs(fireWatchRef);
        const fireWatchList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setFireWatchJobs(fireWatchList);
      } catch (error) {
        console.error("Error fetching fire watch jobs:", error);
      }
    };

    const fetchUsers = async () => {
      try {
        const usersRef = collection(db, "users");
        const querySnapshot = await getDocs(usersRef);
        const usersList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsers(usersList);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    const fetchProperties = async () => {
      try {
        const propertiesRef = collection(db, "properties");
        const querySnapshot = await getDocs(propertiesRef);
        const propertiesList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          propertyName: doc.data().propertyName, // Ensure propertyName is correctly set
          ...doc.data(),
        }));
        // Sort properties alphabetically by propertyName
        propertiesList.sort((a, b) => a.propertyName.localeCompare(b.propertyName));
        setProperties(propertiesList);
      } catch (error) {
        console.error("Error fetching properties:", error);
      }
    };

    fetchFireWatchJobs();
    fetchUsers();
    fetchProperties();
  }, []);

  const formatTime = (time) => {
    const [hour, minute] = time.split(":");
    const ampm = hour >= 12 ? "PM" : "AM";
    const formattedHour = hour % 12 || 12;
    return `${formattedHour}:${minute} ${ampm}`;
  };

  const formatDate = (date) => {
    const [year, month, day] = date.split("-");
    return `${month}-${day}-${year}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewFireWatchJob((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddFireWatchJob = async () => {
    // Check for mandatory fields
    const mandatoryFields = ["siteName", "address"];

    for (const field of mandatoryFields) {
      if (!newFireWatchJob[field]) {
        alert(`Please fill out the ${field} field.`);
        return;
      }
    }

    if (isUploading) {
      alert("Files are still uploading. Please wait.");
      return;
    }

    try {
      const fireWatchRef = collection(db, "fireWatch");
      const docRef = await addDoc(fireWatchRef, newFireWatchJob);
      setFireWatchJobs([...fireWatchJobs, { id: docRef.id, ...newFireWatchJob }]);
      setNewFireWatchJob({
        siteName: "",
        address: "",
        patrolStart: "",
        patrolEnd: "",
        techAssigned: "",
        fileUpload: [], // Reset file upload field
      });
      setShowAddJobPopup(false); // Close the popup after saving
    } catch (error) {
      console.error("Error adding fire watch job:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleEditClick = (job) => {
    setIsEditing(true);
    setEditedJob({ ...job, id: selectedJob.id });  // Ensure it sets the job ID correctly
  };
  

  const handleInputChangeForEdit = (e) => {
    const { name, value } = e.target;
    setEditedJob((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateJob = async () => {
    if (isUploading) {
      alert("Files are still uploading. Please wait.");
      return;
    }
  
    if (!editedJob || !editedJob.id) {
      console.error("Error: No job ID found.");
      return;
    }
  
    try {
      const jobDocRef = doc(db, "fireWatch", editedJob.id);
  
      // Update Firestore with the edited job details
      await updateDoc(jobDocRef, {
        patrolStart: editedJob.patrolStart || "",
        patrolEnd: editedJob.patrolEnd || "",
        techAssigned: editedJob.techAssigned || "",
        fileUpload: editedJob.fileUpload || [],
      });
  
      // Update the local state with the new data
      setFireWatchJobs((prevJobs) =>
        prevJobs.map((job) =>
          job.id === editedJob.id ? { ...job, ...editedJob } : job
        )
      );
  
      // Also update `selectedJob` so the popup shows the updated info
      setSelectedJob((prev) => ({ ...prev, ...editedJob }));
  
      // Exit edit mode and close the popup
      setIsEditing(false);
      setShowDetailsPopup(false);
      alert("Fire Watch job updated successfully!");
    } catch (error) {
      console.error("Error updating fire watch job:", error);
    }
  };

  const downloadExcel = (job) => {
    const rows = [];
  
    rows.push({
      Site: job.siteName,
      Address: job.address,
      Date: "",
      PatrolStart: "",
      PatrolEnd: "",
      Tech: ""
    });
  
    Object.values(job.logs || {}).forEach(log => {
      rows.push({
        Site: "",
        Address: "",
        Date: formatDate(log.date),
        PatrolStart: "",
        PatrolEnd: "",
        Tech: ""
      });
  
      log.patrols.forEach(p => {
        rows.push({
          Site: "",
          Address: "",
          Date: "",
          PatrolStart: formatTime(p.patrolStart),
          PatrolEnd: formatTime(p.patrolEnd),
          Tech: p.techAssigned
        });
      });
    });
  
    const ws = XLSX.utils.json_to_sheet(rows, { skipHeader: false });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Fire Watch Logs");
  
    XLSX.writeFile(
      wb,
      `FireWatch_${job.siteName}.xlsx`
    );
  };
  
  
  
  const generatePatrolsByInterval = (start, end, interval) => {
    const patrols = [];
    let current = new Date(`1970-01-01T${start}`);
    const endTime = new Date(`1970-01-01T${end}`);
  
    while (current < endTime) {
      const next = new Date(current.getTime() + interval * 60000);
  
      patrols.push({
        patrolStart: current.toTimeString().slice(0, 5),
        patrolEnd: next.toTimeString().slice(0, 5),
      });
  
      current = next;
    }
  
    return patrols;
  };
  
  const handleUpdatePatrol = async () => {
    if (isUploading) {
      alert("Files are still uploading. Please wait.");
      return;
    }
  
    if (!editedJob || !editedJob.id) {
      console.error("Error: No job ID found.");
      return;
    }
  
    try {
      const jobDocRef = doc(db, "fireWatch", editedJob.id);
  
      // Update Firestore with the edited patrol details
      await updateDoc(jobDocRef, {
        patrolStart: editedJob.patrolStart || "",
        patrolEnd: editedJob.patrolEnd || "",
        techAssigned: editedJob.techAssigned || "",
        fileUpload: editedJob.fileUpload || [],
      });
  
      // Update the local state with the new data
      setFireWatchJobs((prevJobs) =>
        prevJobs.map((job) =>
          job.id === editedJob.id ? { ...job, ...editedJob } : job
        )
      );
  
      // Also update `selectedJob` so the popup shows the updated info
      setSelectedJob((prev) => ({ ...prev, ...editedJob }));
  
      // Exit edit mode and close the popup
      setIsEditing(false);
      setShowDetailsPopup(false);
      alert("Patrol updated successfully!");
    } catch (error) {
      console.error("Error updating patrol:", error);
    }
  };
  
  const handleCancelEdit = () => {
    setIsEditing(false);
    setShowDetailsPopup(false);
  };

  const handleDeleteConfirmation = () => {
    setShowDeleteConfirmation(true);
  };

  const confirmDelete = async () => {
    try {
      const jobDoc = doc(db, "fireWatch", selectedJob.id);
      await deleteDoc(jobDoc);
      setFireWatchJobs(fireWatchJobs.filter((job) => job.id !== selectedJob.id));
      setShowDetailsPopup(false);
      setSelectedJob(null);
      setShowDeleteConfirmation(false);
    } catch (error) {
      console.error("Error deleting fire watch job:", error);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  const handleDetailsClick = (job) => {
    setSelectedJob({
      ...job,
      patrolStart: job.patrolStart ? formatTime(job.patrolStart) : "",
      patrolEnd: job.patrolEnd ? formatTime(job.patrolEnd) : "",
      date: job.date ? formatDate(job.date) : "",
    });
    setEditedJob({ ...job });
    setShowDetailsPopup(true);
    setIsEditing(false);
  };  

  const handlePatrolClick = (job) => {
    setSelectedJob(job);
    setShowPatrolPopup(true);
  
    // DEFAULT MODE
    setPatrolMode("single");
  
    // RESET FORM
    setNewFireWatchJob({
      patrolStart: "",
      patrolEnd: "",
      techAssigned: "",
      fileUpload: [],
      date: "",
    });
  
    setTimeFrame({
      start: "",
      end: "",
      interval: 15,
    });
  };
  

  const handleAddPatrol = async () => {
    if (!selectedJob || !newFireWatchJob.date) {
      alert("Please select a date for this patrol.");
      return;
    }
  
    let newPatrols = [];
  
    if (patrolMode === "single") {
      const { patrolStart, patrolEnd, techAssigned } = newFireWatchJob;
  
      newPatrols.push({
        patrolStart,
        patrolEnd,
        techAssigned,
        fileUpload: newFireWatchJob.fileUpload || [],
        createdAt: new Date()
      });
    }
  
    if (patrolMode === "range") {
      const generated = generatePatrolsByInterval(
        timeFrame.start,
        timeFrame.end,
        timeFrame.interval
      );
  
      newPatrols = generated.map(p => ({
        ...p,
        techAssigned: newFireWatchJob.techAssigned,
        fileUpload: newFireWatchJob.fileUpload || [],
        createdAt: new Date()
      }));
      setNewFireWatchJob({
        patrolStart: "",
        patrolEnd: "",
        techAssigned: "",
        fileUpload: [],
        date: "",
      });
      
      setTimeFrame({
        start: "",
        end: "",
        interval: 15,
      });
      
      setPatrolMode("single");
      
    }
  
    const dateKey = newFireWatchJob.date;
  
    const updatedLogs = {
      ...(selectedJob.logs || {}),
      [dateKey]: {
        date: dateKey,
        patrols: [
          ...((selectedJob.logs?.[dateKey]?.patrols) || []),
          ...newPatrols
        ]
      }
    };
  
    await updateDoc(doc(db, "fireWatch", selectedJob.id), {
      logs: updatedLogs
    });
  
    setFireWatchJobs(prev =>
      prev.map(j =>
        j.id === selectedJob.id ? { ...j, logs: updatedLogs } : j
      )
    );
  
    setShowPatrolPopup(false);
  };
  

  const handleFileUploadForPatrol = async (e) => {
    const files = e.target.files;
    const uploadedFiles = [];
    setIsUploading(true);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const storageRef = ref(storage, `fireWatch/${newFireWatchJob.siteName}/${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      uploadedFiles.push(downloadURL);
    }

    setNewFireWatchJob((prev) => ({ ...prev, fileUpload: uploadedFiles }));
    setIsUploading(false);
  };

  const closeModal = () => {
    setShowAddJobPopup(false);
    setShowDetailsPopup(false);
    setShowPatrolPopup(false);
    setSelectedJob(null);
    setNewFireWatchJob({
      siteName: "",
      address: "",
      patrolStart: "",
      patrolEnd: "",
      techAssigned: "",
      fileUpload: [],
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const calculateTotalHours = (logs = {}) => {
    let earliest = null;
    let latest = null;
  
    Object.values(logs).forEach(log => {
      log.patrols.forEach(p => {
        const start = new Date(`1970-01-01T${p.patrolStart}`);
        const end = new Date(`1970-01-01T${p.patrolEnd}`);
  
        if (!earliest || start < earliest) earliest = start;
        if (!latest || end > latest) latest = end;
      });
    });
  
    if (!earliest || !latest) return 0;
  
    const diffMs = latest - earliest;
    return (diffMs / (1000 * 60 * 60)).toFixed(2);
  };
  
  const renderPatrols = (patrols) => {
    const itemsPerPage = 20;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedPatrols = patrols.slice(startIndex, endIndex);

    return (
      <>
        {paginatedPatrols.map((patrol, index) => (
          <div key={index} className="patrol-row">
            <p><strong>Patrol Start:</strong><br></br>{formatTime(patrol.patrolStart)}</p>
            <p><strong>Patrol End:</strong><br></br>{formatTime(patrol.patrolEnd)}</p>
            <p><strong>Tech Assigned:</strong><br></br>{patrol.techAssigned}</p>
            {Array.isArray(patrol.fileUpload) && patrol.fileUpload.length > 0 && (
              <p><strong>File Upload:</strong> {patrol.fileUpload.map((file, index) => (
                <a key={index} href={file} target="_blank" rel="noopener noreferrer">File {index + 1}</a>
              ))}</p>
            )}
            <button className="blue-button" onClick={() => handleEditClick(patrol)}>Edit</button>
          </div>
        ))}
        <div className="pagination-buttons">
          {currentPage > 1 && (
            <button className="blue-button" onClick={() => handlePageChange(currentPage - 1)}>Previous</button>
          )}
          {endIndex < patrols.length && (
            <button className="blue-button" onClick={() => handlePageChange(currentPage + 1)}>Next</button>
          )}
        </div>
      </>
    );
  };

  const handlePrint = () => {
    window.print();
  };

  const handleAddFireWatchLog = async () => {
    // Check for mandatory fields
    const mandatoryFields = ["siteName", "address", "date"]; // Add date to mandatory fields

    for (const field of mandatoryFields) {
      if (!newFireWatchJob[field]) {
        alert(`Please fill out the ${field} field.`);
        return;
      }
    }

    if (isUploading) {
      alert("Files are still uploading. Please wait.");
      return;
    }

    try {
      const fireWatchRef = collection(db, "fireWatch");
      const docRef = await addDoc(fireWatchRef, newFireWatchJob);
      setFireWatchJobs([...fireWatchJobs, { id: docRef.id, ...newFireWatchJob }]);
      setNewFireWatchJob({
        siteName: "",
        address: "",
        patrolStart: "",
        patrolEnd: "",
        techAssigned: "",
        fileUpload: [], // Reset file upload field
        date: "", // Reset date field
      });
      setShowAddJobPopup(false); // Close the popup after saving
      alert("Firewatch Log Added");
    } catch (error) {
      console.error("Error adding fire watch log:", error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="firewatch-page">
      <h1>Fire Watch</h1>
      {(userRole === "Admin" || userRole === "Manager") && (
        <button className="blue-button" onClick={() => setShowAddJobPopup(true)}>Click Here to Add Fire Watch Job</button>
      )}
      <button className="blue-button" onClick={() => setShowAddJobPopup(true)}>Add Fire Watch Log</button>

      {showAddJobPopup && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Add Fire Watch Job</h2>
            <div className="form-grid">
              {/* Input Fields */}
              <div>
                <label>Site Name <span className="required">*</span></label>
                <select name="siteName" onChange={handleInputChange} value={newFireWatchJob.siteName}>
                  <option value="">Select Site</option>
                  {properties.map((property) => (
                    <option key={property.id} value={property.propertyName}>
                      {property.propertyName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label>Address <span className="required">*</span></label>
                <input name="address" onChange={handleInputChange} value={newFireWatchJob.address} />
              </div>
              <div>
                <label>Date <span className="required">*</span></label>
                <input type="date" name="date" onChange={handleInputChange} value={newFireWatchJob.date} />
              </div>
            </div>
            <div className="modal-buttons">
              <button className="blue-button" onClick={handleAddFireWatchLog} disabled={isUploading}>
                {isUploading ? "Uploading..." : "Save"}
              </button>
              <button className="red-button" onClick={closeModal}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showDetailsPopup && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Fire Watch Details</h2>
            <div className="popup-content">
              <h3 className="company-name">Dynasty Community Services</h3>
              <h4>Fire Watch Logs</h4>
              <p><strong>Site Name:</strong> {selectedJob.siteName}</p>
              <p><strong>Address:</strong> {selectedJob.address}</p>
              <p><strong>Date:</strong> {selectedJob.date}</p> {/* Add date field */}
              <p>
  <strong>Total Fire Watch Hours:</strong>{" "}
  {calculateTotalHours(selectedJob.logs)} hrs
</p>

              {isEditing ? (
                <div className="form-grid">
                 {/* SINGLE PATROL FIELDS */}
{patrolMode === "single" && (
  <>
    <div>
      <label>Patrol Start</label>
      <input
        name="patrolStart"
        type="time"
        value={newFireWatchJob.patrolStart}
        onChange={handleInputChange}
      />
    </div>

    <div>
      <label>Patrol End</label>
      <input
        name="patrolEnd"
        type="time"
        value={newFireWatchJob.patrolEnd}
        onChange={handleInputChange}
      />
    </div>
  </>
)}

                  <div>
                    <label>Tech Assigned</label>
                    <select name="techAssigned" value={editedJob.techAssigned || ""} onChange={handleInputChangeForEdit}>
                      <option value="">Select Tech</option>
                      {users.map((user) => (
                        <option key={user.id} value={`${user.firstName} ${user.lastName}`}>
                          {user.firstName} {user.lastName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label>File Upload</label>
                    <input type="file" multiple accept=".pdf,.jpeg,.jpg,.hic,.png,.xslx" onChange={handleFileUploadForEdit} />
                    {Array.isArray(editedJob.fileUpload) && editedJob.fileUpload.map((file, index) => (
                      <div key={index} className="file-item">
                        <a href={file} target="_blank" rel="noopener noreferrer">File {index + 1}</a>
                        <button className="delete-button" onClick={() => handleDeleteImage(index)}>X</button>
                      </div>
                    ))}
                  </div>
                  <div className="modal-buttons">
                    <button className="blue-button" onClick={handleUpdatePatrol} disabled={isUploading}>
                      {isUploading ? "Uploading..." : "Update"}
                    </button>
                    <button className="red-button" onClick={handleCancelEdit}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  {Object.entries(selectedJob.logs || {}).map(([date, log]) => (
  <div key={date} className="date-section">
    <h4>📅 {formatDate(date)}</h4>
    {renderPatrols(log.patrols)}
  </div>
))}
                  <div className="modal-buttons">
                    <button className="red-button" onClick={handleDeleteConfirmation}>Delete</button>
                    <button className="green-button" onClick={closeModal}>Close</button>
                    <button className="blue-button" onClick={handlePrint}>Print</button>
                    <button
  className="blue-button"
  onClick={() => downloadExcel(selectedJob)}
>
  Download Excel
</button>

                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showPatrolPopup && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Add Patrol</h2>
            <div className="form-grid">
            <div className="toggle-row">
  <button
    className={patrolMode === "single" ? "active" : ""}
    onClick={() => setPatrolMode("single")}
  >
    Single Patrol
  </button>
  <button
    className={patrolMode === "range" ? "active" : ""}
    onClick={() => setPatrolMode("range")}
  >
    Time Frame
  </button>
</div>
<div>
  <label>Log Date</label>
  <input
    type="date"
    value={newFireWatchJob.date}
    onChange={(e) =>
      setNewFireWatchJob(prev => ({
        ...prev,
        date: e.target.value
      }))
    }
  />
</div>

              <div>
                <label>Patrol Start</label>
                <input name="patrolStart" type="time" onChange={handleInputChange} value={newFireWatchJob.patrolStart} />
              </div>
              <div>
                <label>Patrol End</label>
                <input name="patrolEnd" type="time" onChange={handleInputChange} value={newFireWatchJob.patrolEnd} />
              </div>
              {patrolMode === "range" && (
  <>
    <div>
      <label>Time Frame Start</label>
      <input
        type="time"
        value={timeFrame.start}
        onChange={(e) =>
          setTimeFrame({ ...timeFrame, start: e.target.value })
        }
      />
    </div>

    <div>
      <label>Time Frame End</label>
      <input
        type="time"
        value={timeFrame.end}
        onChange={(e) =>
          setTimeFrame({ ...timeFrame, end: e.target.value })
        }
      />
    </div>

    <div>
      <label>Interval (minutes)</label>
      <input
        type="number"
        min="5"
        step="5"
        value={timeFrame.interval}
        onChange={(e) =>
          setTimeFrame({ ...timeFrame, interval: Number(e.target.value) })
        }
      />
    </div>
  </>
)}


              <div>
                <label>Tech Assigned</label>
                <select name="techAssigned" onChange={handleInputChange} value={newFireWatchJob.techAssigned}>
                  <option value="">Select Tech</option>
                  {users.map((user) => (
                    <option key={user.id} value={`${user.firstName} ${user.lastName}`}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label>File Upload</label>
                <input type="file" multiple accept=".pdf,.jpeg,.jpg,.hic,.png,.xslx" onChange={handleFileUploadForPatrol} />
              </div>
            </div>
            <div className="modal-buttons">
              <button className="blue-button" onClick={handleAddPatrol}>Save</button>
              <button className="red-button" onClick={closeModal}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirmation && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Are you sure you want to delete?</h2>
            <div className="modal-buttons">
              <button className="red-button" onClick={confirmDelete}>Yes</button>
              <button className="blue-button" onClick={cancelDelete}>No</button>
            </div>
          </div>
        </div>
      )}

      {/* Fire Watch List */}
      <h2>Fire Watch List</h2>
      <table className="firewatch-table">
        <thead>
          <tr>
            <th>Site Name</th>
            <th>Address</th>
            <th>Date</th> {/* Add date column */}
            <th>Patrol</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          {fireWatchJobs.map((job) => (
            <React.Fragment key={job.id}>
              <tr>
                <td>{job.siteName}</td>
                <td>{job.address}</td>
                <td>{job.date}</td> {/* Add date field */}
                <td>
                  <button className="blue-button" onClick={() => handlePatrolClick(job)}>
                    Patrol
                  </button>
                </td>
                <td>
                  <button className="blue-button" onClick={() => handleDetailsClick(job)}>
                    Details
                  </button>
                </td>
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default FireWatch;